import React, { useMemo, useCallback, useState } from "react";
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
  FormHelperText,
  InputLabel,
  CircularProgress,
  TextField,
  Box,
} from "@mui/material";
import {
  Controller,
  Control,
  FieldError,
  FieldValues,
  ControllerRenderProps,
  Path,
} from "react-hook-form";
import { SelectChangeEvent } from "@mui/material";

interface CountryCode {
  code: string;
  flag: string;
  name?: string;
}

interface PhoneInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  countryCodes: CountryCode[];
  label?: string;
  error?: FieldError;
  disabled?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  placeholder?: string;
}

interface PhoneInputRendererProps<TFieldValues extends FieldValues = FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  countryCodes: CountryCode[];
  label?: string;
  error?: FieldError;
  disabled?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  placeholder?: string;
}

const PhoneInputRenderer = <TFieldValues extends FieldValues = FieldValues>({
  field,
  countryCodes = [],
  label,
  error,
  disabled,
  isLoading = false,
  hasError = false,
  margin = 'none',
  placeholder,
}: PhoneInputRendererProps<TFieldValues>) => {
  const { value, onChange } = field;
  const [search, setSearch] = useState('');

  const [countryCode, phoneNumber] = useMemo(() => {
    if (!value) return ["", ""];
    const parts = String(value).split(" ");
    return [parts[0] || "", parts[1] ?? ""];
  }, [value]);

  const sortedCodes = useMemo(() => {
    return [...countryCodes].sort((a, b) => a.flag.localeCompare(b.flag));
  }, [countryCodes]);

  const filteredCodes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedCodes;

    // Match on region code or dial code only — not the country name.
    // Region codes are unique 2-letter codes, so typing "in" shows only "IN +91".
    const digits = q.replace(/\D/g, '');
    return sortedCodes.filter(
      (c) =>
        c.flag.toLowerCase().includes(q) ||
        (!!digits && c.code.replace('+', '').startsWith(digits))
    );
  }, [sortedCodes, search]);

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = e.target.value.replace(/\D/g, "");
      const newValue = `${countryCode} ${cleaned}`.trim();
      onChange(newValue);

    },
    [countryCode, onChange]
  );

  const handleCodeChange = useCallback(
    (e: SelectChangeEvent) => {
      const newValue = `${e.target.value} ${phoneNumber}`.trim();
      onChange(newValue);
    },
    [phoneNumber, onChange]
  );

  return (
    <FormControl 
      fullWidth 
      variant="outlined" 
      error={!!error}
      margin={margin}
    >
      <InputLabel>{label}</InputLabel>

      <OutlinedInput
        label={label}
        value={phoneNumber}
        onChange={handleNumberChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || hasError}
        inputProps={{
          maxLength: 15,
          inputMode: "numeric",
        }}
        startAdornment={
          <InputAdornment position="start">
            <Select
              value={countryCode}
              onChange={handleCodeChange}
              variant="standard"
              disableUnderline
              disabled={disabled || isLoading || hasError}
              MenuProps={{ autoFocus: false }}
              onClose={() => setSearch('')}
              sx={{
                width: 100,
                '& .MuiSelect-select': {
                  padding: '8px 4px',
                  fontSize: '14px',
                  display: 'flex',
                  height: '30px',
                  alignItems: 'center',
                },
              }}
            >
              {isLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading...
                </MenuItem>
              ) : hasError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Error loading codes
                </MenuItem>
              ) : countryCodes.length === 0 ? (
                <MenuItem disabled>No codes available</MenuItem>
              ) : [
                <Box
                  key="search-box"
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ px: 1, pt: 1, pb: 0.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search region/code..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>,
                <MenuItem key="select-code" value="" disabled>
                  Select code
                </MenuItem>,
                ...(filteredCodes.length === 0
                  ? [
                      <MenuItem key="no-match" disabled>
                        No match found
                      </MenuItem>,
                    ]
                  : filteredCodes.map((c) => (
                      <MenuItem key={`${c.flag}-${c.code}`} value={c.code}>
                        {`${c.flag} ${c.code}`}
                      </MenuItem>
                    )))
              ]}
            </Select>
          </InputAdornment>
        }
      />

      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
};

export default function PhoneInput<TFieldValues extends FieldValues = FieldValues>(props: PhoneInputProps<TFieldValues>) {
  const { name, control, countryCodes, ...rest } = props;

  return (
    <Controller
      name={name}
      control={control}
       rules={{
        validate: (value) => {
          if (!value || !value.trim()) {
            return true; // Allow empty (optional field)
          }

          const parts = value.trim().split(" ");
          const countryCode = parts[0] || "";
          const number = parts[1] || "";

          // Validate country code format (must start with + and contain digits)
          if (countryCode && !/^\+\d+$/.test(countryCode)) {
            return "Invalid country code format";
          }

          // Validate that country code exists in the available list
          if (countryCode && countryCodes.length > 0) {
            const isValidCode = countryCodes.some(c => c.code === countryCode);
            if (!isValidCode) {
              return "Invalid country code";
            }
          }

          // If user entered a number, country code is required
          if (number && !countryCode) {
            return "Please select a country code";
          }

          // If country code is selected, number is required
          if (countryCode && !number) {
            return "Phone number is required";
          }

          // Validate national number contains only digits
          if (number && !/^\d+$/.test(number)) {
            return "Phone number must contain only digits";
          }

          // Validate phone number length (E.164 standard: 4-15 digits)
          if (number && number.length < 4) {
            return "Phone number is too short (minimum 4 digits)";
          }

          if (number && number.length > 15) {
            return "Phone number is too long (maximum 15 digits)";
          }

          return true;
        },
      }}
      render={({ field, fieldState }) => (
        <PhoneInputRenderer<TFieldValues>
          countryCodes={countryCodes}
          field={field}
          error={fieldState.error}
          {...rest} 
        />
      )}
    />
  );
}
